import { Button, Flex } from "@chakra-ui/react";
import { t } from "i18next";
import { ContractReportQueryDto } from "../../../autogen/bff-api";
import {
  buildActiveFilters,
  buildAwardedFilters,
  buildExpiredFilters,
  buildExpiringThisYearFilters,
  buildTerminatedFilters,
} from "./contractStatusFilters";

interface Props {
  query: ContractReportQueryDto;
  onSetFilters: (q: Partial<ContractReportQueryDto>) => void;
}

export const ContractStatusShortcuts = ({ query, onSetFilters }: Props) => {
  const sanitize = (q: ContractReportQueryDto): Partial<ContractReportQueryDto> => {
    const {
      statuses,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      terminationDateFrom,
      terminationDateTo,
      hasNoEndDate,
    } = q;
    return {
      statuses,
      startDateFrom,
      startDateTo,
      endDateFrom,
      endDateTo,
      terminationDateFrom,
      terminationDateTo,
      hasNoEndDate,
    };
  };

  const allowedKeys = new Set<keyof ContractReportQueryDto>([
    "owningOrganizationId",
    "limit",
    "skip",
    "statuses",
    "startDateFrom",
    "startDateTo",
    "endDateFrom",
    "endDateTo",
    "terminationDateFrom",
    "terminationDateTo",
    "hasNoEndDate",
  ]);

  const isActiveFilterValue = (value: unknown): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "boolean") return value === true;
    if (typeof value === "number") return true;
    return true;
  };

  const hasNoOtherActiveFilters = Object.entries(query).every(([key, value]) => {
    return allowedKeys.has(key as keyof ContractReportQueryDto) || !isActiveFilterValue(value);
  });

  const awardedExpected = buildAwardedFilters({ base: {} });
  const activeExpected = buildActiveFilters({ base: {} });
  const expiredExpected = buildExpiredFilters({ base: {} });
  const expiringYearExpected = buildExpiringThisYearFilters({ base: {} });
  const terminatedExpected = buildTerminatedFilters({ base: {} });

  const sanitized = sanitize(query);
  const isAwardedSelected = hasNoOtherActiveFilters && JSON.stringify(awardedExpected) === JSON.stringify(sanitized);
  const isActiveSelected = hasNoOtherActiveFilters && JSON.stringify(activeExpected) === JSON.stringify(sanitized);
  const isExpiredSelected = hasNoOtherActiveFilters && JSON.stringify(expiredExpected) === JSON.stringify(sanitized);
  const isExpiringYearSelected =
    hasNoOtherActiveFilters && JSON.stringify(expiringYearExpected) === JSON.stringify(sanitized);
  const isTerminatedSelected =
    hasNoOtherActiveFilters && JSON.stringify(terminatedExpected) === JSON.stringify(sanitized);

  const setAwarded = () => onSetFilters(awardedExpected);
  const setActive = () => onSetFilters(activeExpected);
  const setExpired = () => onSetFilters(expiredExpected);
  const setExpiringYear = () => onSetFilters(expiringYearExpected);
  const setTerminated = () => onSetFilters(terminatedExpected);

  return (
    <Flex gap="2" mb="3">
      <Button size="sm" variant={isAwardedSelected ? "solid" : "outline"} onClick={setAwarded}>
        {t("Awarded")}
      </Button>
      <Button size="sm" variant={isActiveSelected ? "solid" : "outline"} onClick={setActive}>
        {t("Active")}
      </Button>
      <Button size="sm" variant={isExpiredSelected ? "solid" : "outline"} onClick={setExpired}>
        {t("Expired")}
      </Button>
      <Button size="sm" variant={isExpiringYearSelected ? "solid" : "outline"} onClick={setExpiringYear}>
        {t("Expiring this year")}
      </Button>
      <Button size="sm" variant={isTerminatedSelected ? "solid" : "outline"} onClick={setTerminated}>
        {t("Terminated")}
      </Button>
    </Flex>
  );
};
